'use client';

import React, { useCallback, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

export interface RangeSliderProps {
  /** Minimum value */
  min: number;
  /** Maximum value */
  max: number;
  /** Current value as [minVal, maxVal] */
  value: [number, number];
  /** Called when value changes */
  onChange: (value: [number, number]) => void;
  /** Step for thumb movement */
  step?: number;
  disabled?: boolean;
  className?: string;
  /** Optional labels shown below track ends (e.g. "0", "3000") */
  showTrackLabels?: boolean;
  /** Accessible names of the two handles. */
  minLabel?: string;
  maxLabel?: string;
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}

function valueToPercent(value: number, min: number, max: number): number {
  if (max === min) return 0;
  return ((value - min) / (max - min)) * 100;
}

export function RangeSlider({
  min,
  max,
  value: [rawLow, rawHigh],
  onChange,
  step = 1,
  disabled = false,
  className,
  showTrackLabels = true,
  minLabel = 'Minimum value',
  maxLabel = 'Maximum value',
}: RangeSliderProps) {
  const range = Math.max(0, max - min);
  const upperBound = min + range;
  const stepSize = Number.isFinite(step) && step > 0 ? step : 1;
  const first = Number.isFinite(rawLow) ? clamp(rawLow, min, upperBound) : min;
  const second = Number.isFinite(rawHigh) ? clamp(rawHigh, min, upperBound) : upperBound;
  const low = Math.min(first, second);
  const high = Math.max(first, second);
  const lowPct = valueToPercent(low, min, max);
  const highPct = valueToPercent(high, min, max);
  const trackRef = useRef<HTMLDivElement>(null);
  const [drag, setDrag] = useState<{ thumb: 'low' | 'high'; pointerId: number } | null>(null);

  const move = useCallback(
    (thumb: 'low' | 'high', next: number) => {
      if (disabled) return;
      const values: [number, number] =
        thumb === 'low' ? [clamp(next, min, high), high] : [low, clamp(next, low, upperBound)];
      if (values[0] !== rawLow || values[1] !== rawHigh) onChange(values);
    },
    [disabled, min, upperBound, low, high, rawLow, rawHigh, onChange],
  );

  const percentToValue = useCallback(
    (pct: number): number => {
      const v = min + (pct / 100) * range;
      if (v <= min) return min;
      if (v >= upperBound) return upperBound;
      const stepped = Number((min + Math.round((v - min) / stepSize) * stepSize).toPrecision(12));
      return clamp(stepped, min, upperBound);
    },
    [min, range, stepSize, upperBound],
  );

  const pointerValue = useCallback(
    (clientX: number) => {
      if (!trackRef.current) return null;
      const rect = trackRef.current.getBoundingClientRect();
      if (rect.width <= 0) return null;
      return percentToValue(clamp(((clientX - rect.left) / rect.width) * 100, 0, 100));
    },
    [percentToValue],
  );

  const startDrag = (e: React.PointerEvent<HTMLDivElement>, thumb?: 'low' | 'high') => {
    if (disabled || drag || e.button !== 0 || !e.isPrimary) return;
    e.preventDefault();
    e.stopPropagation();
    const v = pointerValue(e.clientX);
    // A track tap opens coincident endpoints toward the tap; overlapping max handles drag left.
    const chosen =
      !thumb && low === high && v !== null
        ? v > high
          ? 'high'
          : 'low'
        : low === high && high === upperBound
          ? 'low'
          : (thumb ?? (v !== null && Math.abs(v - low) > Math.abs(v - high) ? 'high' : 'low'));
    setDrag({ thumb: chosen, pointerId: e.pointerId });
    trackRef.current
      ?.querySelectorAll<HTMLElement>('[role="slider"]')
      [chosen === 'low' ? 0 : 1]?.focus({ preventScroll: true });
    // Settle the number input's blur before a track tap schedules the next range draft.
    if (!thumb && v !== null) move(chosen, v);
  };

  const handleKeyDown = (thumb: 'low' | 'high') => (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    const current = thumb === 'low' ? low : high;
    let next: number;
    if (e.key === 'Home') next = thumb === 'low' ? min : low;
    else if (e.key === 'End') next = thumb === 'low' ? high : upperBound;
    else {
      const direction = ['ArrowRight', 'ArrowUp', 'PageUp'].includes(e.key)
        ? 1
        : ['ArrowLeft', 'ArrowDown', 'PageDown'].includes(e.key)
          ? -1
          : 0;
      if (!direction) return;
      const count = e.key.startsWith('Page') ? 10 : 1;
      const index = (current - min) / stepSize;
      const adjacent = direction > 0 ? Math.floor(index + 1e-9) : Math.ceil(index - 1e-9);
      next = Number((min + (adjacent + direction * count) * stepSize).toPrecision(12));
    }
    e.preventDefault();
    move(thumb, next);
  };

  React.useEffect(() => {
    if (!drag) return;
    if (disabled) {
      setDrag(null);
      return;
    }
    const onMove = (e: PointerEvent) => {
      if (e.pointerId !== drag.pointerId) return;
      const v = pointerValue(e.clientX);
      if (v !== null) move(drag.thumb, v);
    };
    const onEnd = (e: PointerEvent) => {
      if (e.pointerId === drag.pointerId) setDrag(null);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onEnd);
    window.addEventListener('pointercancel', onEnd);
    window.addEventListener('lostpointercapture', onEnd);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onEnd);
      window.removeEventListener('pointercancel', onEnd);
      window.removeEventListener('lostpointercapture', onEnd);
    };
  }, [drag, disabled, pointerValue, move]);

  const thumb = (which: 'low' | 'high', position: number, label: string) => (
    <div
      role="slider"
      aria-valuemin={which === 'low' ? min : low}
      aria-valuemax={which === 'low' ? high : upperBound}
      aria-valuenow={which === 'low' ? low : high}
      aria-label={label}
      aria-orientation="horizontal"
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      className="focus-visible:ring-ring absolute top-1/2 z-10 flex h-11 w-11 -translate-x-1/2 -translate-y-1/2 cursor-grab items-center justify-center rounded-full focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline active:cursor-grabbing"
      style={{ left: `${position}%` }}
      onPointerDown={(e) => startDrag(e, which)}
      onKeyDown={handleKeyDown(which)}
    >
      <span
        aria-hidden="true"
        className="border-primary bg-background h-4 w-4 rounded-full border-2 shadow-sm"
      />
    </div>
  );

  return (
    <div className={cn('w-full', className)}>
      <div
        ref={trackRef}
        role="group"
        aria-label="Range slider"
        onPointerDown={startDrag}
        className={cn(
          'relative h-11 w-full touch-none',
          disabled && 'pointer-events-none opacity-50',
        )}
      >
        {/* Track background */}
        <div className="bg-border absolute top-1/2 left-0 h-1.5 w-full -translate-y-1/2 cursor-pointer rounded-full" />
        {/* Filled segment */}
        <div
          className="bg-primary pointer-events-none absolute top-1/2 left-0 h-1.5 -translate-y-1/2 rounded-full transition-[left,width] duration-75"
          style={{ left: `${lowPct}%`, width: `${highPct - lowPct}%` }}
        />
        {/* Low thumb */}
        {thumb('low', lowPct, minLabel)}
        {/* High thumb */}
        {thumb('high', highPct, maxLabel)}
      </div>
      {showTrackLabels && (
        <div className="text-muted-foreground mt-1 flex justify-between text-xs">
          <span>{min}</span>
          <span>{max}</span>
        </div>
      )}
    </div>
  );
}
